import { FC, ReactNode } from 'react'

import { Box, Circle, Flex, HStack, IconButton, Spacer, Square, Text } from '@chakra-ui/react'
import { AlertFillIcon, CollapseIcon, DeleteIcon, ExpandIcon, HideIcon, ShowIcon } from '@editor/assets/icons'
import { LAYER_ITEM_STYLES } from '@editor/config/styles'
import { Tooltip } from '@infraloop/common'
import { isTablet } from 'react-device-detect'

const LayerItem: FC<{
  label: ReactNode
  collapsed?: boolean
  collapsible?: boolean
  invisible?: boolean
  selected?: boolean
  disabled?: boolean
  errorMessage?: string
  colorLabel?: string
  onClick?: () => void
  onMouseOver?: () => void
  onMouseOut?: () => void
  updateVisibility?: (invisible: boolean) => void
  updateExpansion?: (expanded: boolean) => void
  deleteLayer?: () => void
  isChild?: boolean
}> = ({
  label,
  collapsible,
  collapsed,
  invisible,
  selected,
  disabled,
  errorMessage,
  colorLabel,
  onClick,
  onMouseOver,
  onMouseOut,
  updateVisibility,
  updateExpansion = () => null,
  deleteLayer,
  isChild,
}) => {
  const iconSize = isTablet ? 'sm' : 'xs'
  const fontSize = isTablet ? 'md' : 'sm'

  const className = [onClick && !disabled ? 'editor-clickable-item' : '']
  if (selected) className.push('selected')

  return (
    <Flex
      position="relative"
      className={className.join(' ')}
      backgroundColor={selected ? 'whiteAlpha.200' : 'transparent'}
      w="full"
      alignItems="center"
      {...LAYER_ITEM_STYLES}
      onClick={(e) => {
        if (onClick) {
          e.stopPropagation()
          onClick()
        }
      }}
      onMouseOver={() => {
        if (onMouseOver) onMouseOver()
      }}
      onMouseOut={() => {
        if (onMouseOut) onMouseOut()
      }}
      data-testid="layer-item"
    >
      {/* Color label */}
      {colorLabel && <Circle size="1em" bg={colorLabel} />}

      {/* Main text */}
      <Text pl={isChild ? 3 : 0} whiteSpace="nowrap">
        {label}
      </Text>

      <Spacer />

      <HStack gap={0}>
        {/* Visibility toggle */}
        {updateVisibility && !invisible && (
          <IconButton
            minW="auto"
            width="20px"
            height="20px"
            aria-label="hide"
            fontSize={fontSize}
            onClick={(e) => {
              e.stopPropagation()
              updateVisibility(false)
            }}
            size={iconSize}
            variant="toolbarIcon"
            disabled={disabled}
          >
            <ShowIcon />
          </IconButton>
        )}

        {updateVisibility && invisible && (
          <IconButton
            minW="auto"
            width="20px"
            height="20px"
            aria-label="show"
            fontSize={fontSize}
            onClick={(e) => {
              e.stopPropagation()
              updateVisibility(true)
            }}
            size={iconSize}
            variant="toolbarIcon"
          >
            <HideIcon />
          </IconButton>
        )}

        {/* Delete button */}
        {deleteLayer && !disabled && (
          <IconButton
            minW="auto"
            width="20px"
            height="20px"
            aria-label="delete"
            fontSize={fontSize}
            onClick={(e) => {
              e.stopPropagation()
              deleteLayer()
            }}
            size={iconSize}
            variant="toolbarIcon"
          >
            <DeleteIcon />
          </IconButton>
        )}

        {deleteLayer && disabled && <Square size={6} />}
        {collapsible && (
          <IconButton
            minW="auto"
            width="20px"
            height="20px"
            aria-label={collapsed ? 'expand' : 'collapse'}
            fontSize={fontSize}
            onClick={(e) => {
              e.stopPropagation()
              if (updateExpansion) updateExpansion(!collapsed)
            }}
            size={iconSize}
            variant="toolbarIcon"
          >
            {collapsed ? <ExpandIcon /> : <CollapseIcon />}
          </IconButton>
        )}
      </HStack>

      {/* Error message */}
      {!!errorMessage && (
        <Tooltip
          showArrow
          positioning={{ placement: 'right' }}
          label={
            errorMessage
          } /* TODO: style migration needed - p=2 */ /* TODO: style migration needed - fontSize=xs */ /* TODO: style migration needed - fontWeight=normal */
        >
          <Box position="absolute" left={2} top={2} fontSize="80%">
            <AlertFillIcon color="orange" />
          </Box>
        </Tooltip>
      )}
    </Flex>
  )
}

export default LayerItem
